import { create } from 'zustand';
import type { User } from '@supabase/supabase-js';
import type { Patient, Doctor, Appointment, Invoice, UserProfile } from './supabase';

interface AppState {
  // Auth
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  setUser: (user: User | null) => void;
  setUserProfile: (profile: UserProfile | null) => void;
  setLoading: (loading: boolean) => void;

  currency: string;
  currencySymbol: string;
  setCurrency: (currency: string, symbol: string) => void;

  // Patients
  patients: Patient[];
  setPatients: (patients: Patient[]) => void;
  addPatient: (patient: Patient) => void;
  updatePatient: (id: string, patient: Partial<Patient>) => void;
  removePatient: (id: string) => void;

  // Doctors
  doctors: Doctor[];
  setDoctors: (doctors: Doctor[]) => void;
  addDoctor: (doctor: Doctor) => void;
  updateDoctor: (id: string, doctor: Partial<Doctor>) => void;
  removeDoctor: (id: string) => void;

  // Appointments
  appointments: Appointment[];
  setAppointments: (appointments: Appointment[]) => void;
  addAppointment: (appointment: Appointment) => void;
  updateAppointment: (id: string, appointment: Partial<Appointment>) => void;
  removeAppointment: (id: string) => void;

  // Invoices
  invoices: Invoice[];
  setInvoices: (invoices: Invoice[]) => void;
  addInvoice: (invoice: Invoice) => void;
  updateInvoice: (id: string, invoice: Partial<Invoice>) => void;
  removeInvoice: (id: string) => void;
}

export const useStore = create<AppState>((set) => ({
  // Auth
  user: null,
  userProfile: null,
  loading: true,
  setUser: (user) => set({ user }),
  setUserProfile: (userProfile) => set({ userProfile }),
  setLoading: (loading) => set({ loading }),

  currency: 'BDT',
  currencySymbol: '৳',
  setCurrency: (currency, currencySymbol) => set({ currency, currencySymbol }),

  // Patients
  patients: [],
  setPatients: (patients) => set({ patients }),
  addPatient: (patient) => set((state) => ({ patients: [patient, ...state.patients] })),
  updatePatient: (id, updatedPatient) => set((state) => ({
    patients: state.patients.map(p => p.id === id ? { ...p, ...updatedPatient } : p)
  })),
  removePatient: (id) => set((state) => ({
    patients: state.patients.filter(p => p.id !== id)
  })),

  // Doctors
  doctors: [],
  setDoctors: (doctors) => set({ doctors }),
  addDoctor: (doctor) => set((state) => ({ doctors: [doctor, ...state.doctors] })),
  updateDoctor: (id, updatedDoctor) => set((state) => ({
    doctors: state.doctors.map(d => d.id === id ? { ...d, ...updatedDoctor } : d)
  })),
  removeDoctor: (id) => set((state) => ({
    doctors: state.doctors.filter(d => d.id !== id)
  })),

  // Appointments
  appointments: [],
  setAppointments: (appointments) => set({ appointments }),
  addAppointment: (appointment) => set((state) => ({ appointments: [appointment, ...state.appointments] })),
  updateAppointment: (id, updatedAppointment) => set((state) => ({
    appointments: state.appointments.map(a => a.id === id ? { ...a, ...updatedAppointment } : a)
  })),
  removeAppointment: (id) => set((state) => ({
    appointments: state.appointments.filter(a => a.id !== id)
  })),

  // Invoices
  invoices: [],
  setInvoices: (invoices) => set({ invoices }),
  addInvoice: (invoice) => set((state) => ({ invoices: [invoice, ...state.invoices] })),
  updateInvoice: (id, updatedInvoice) => set((state) => ({
    invoices: state.invoices.map(i => i.id === id ? { ...i, ...updatedInvoice } : i)
  })),
  removeInvoice: (id) => set((state) => ({
    invoices: state.invoices.filter(i => i.id !== id)
  })),
}));
